/**
 * Code de service PAR LANGUE — l'identifiant technique n'est pas fait pour être lu.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * `serviceTypes[].id` est la CLÉ : elle vit en base, relie les guichets aux tickets, et
 * `formatTicketId()` la concatène au numéro pour former `RET13`, identifiant unique du ticket.
 * Elle ne peut donc pas changer — la traduire casserait l'identification.
 *
 * Mais elle n'a jamais été faite pour être MONTRÉE ni PRONONCÉE. Sur un écran arabe, `RET13`
 * reste en caractères latins ; et la phrase d'appel `تذكرة رقم RET13` fait buter le moteur de
 * synthèse, qui n'a aucune idée de ce qu'est « RET ».
 *
 * D'où la séparation : la clé reste technique, et chaque service porte en plus un code
 * d'AFFICHAGE par langue (`codeAr` / `codeEn` / `codeFr`). L'écran et la voix recomposent le
 * libellé avec `ticket.number` — déjà stocké par service — sans jamais toucher à `ticketId`.
 *
 * Conséquence voulue : AUCUNE migration. En base, rien ne bouge.
 */

const CLE_PAR_LANGUE = { ar: 'codeAr', en: 'codeEn', fr: 'codeFr' };

/**
 * Code d'affichage d'un service dans une langue donnée.
 * Repli sur l'identifiant technique : un service sans code traduit reste lisible.
 */
export function serviceCodeFor(config, serviceType, lang = 'fr') {
  const id = String(serviceType ?? '');
  const service = (config?.serviceTypes || []).find((s) => String(s.id) === id);
  if (!service) return id;
  const cle = CLE_PAR_LANGUE[String(lang).slice(0, 2).toLowerCase()] || 'codeFr';
  const code = service[cle];
  return code && String(code).trim() ? String(code).trim() : id;
}

/**
 * Libellé d'un ticket tel qu'il doit être LU dans une langue.
 *
 * Repli sur `ticketId` si le ticket ne porte pas son numéro : mieux vaut afficher le code
 * technique que rien du tout — un client attend son tour, il lui faut un repère.
 */
/**
 * Miroir de `separateurCodeNumero` (apps/server/src/queue/ticket-label.ts, qui fait foi) : l'espace
 * saute si le code finit par un chiffre (`E1` + `3` = `E13`, sinon lu « E un trois »).
 */
export function separateurCodeNumero(code) {
  return /\d$/.test(String(code)) ? '' : ' ';
}

export function ticketLabel(config, ticket, lang = 'fr') {
  if (!ticket) return '';
  const numero = ticket.number;
  if (numero === undefined || numero === null || !ticket.serviceType) {
    return String(ticket.ticketId ?? '');
  }
  // Espace entre le code et le numéro (voir `separateurCodeNumero` pour le cas où elle saute) :
  // `إستقبال20` se lit mal ; `ticketId` (clé en base) reste sans espace.
  const code = serviceCodeFor(config, ticket.serviceType, lang);
  return `${code}${separateurCodeNumero(code)}${numero}`;
}

/**
 * Libellé d'un texte trilingue de la console (`{ ar, fr, en }`) dans une langue,
 * avec repli sur le français puis sur la première valeur renseignée — un écran ne doit
 * jamais rester vide parce qu'une traduction manque.
 */
export function libelle(valeur, lang = 'fr', defaut = '') {
  if (typeof valeur === 'string') return valeur || defaut;
  if (!valeur || typeof valeur !== 'object') return defaut;
  const k = String(lang).slice(0, 2).toLowerCase();
  return valeur[k] || valeur.fr || valeur.ar || valeur.en || defaut;
}

/**
 * Valeurs de repli des libellés d'écran, PAR LANGUE.
 *
 * Elles servent bien plus souvent qu'on ne l'imagine : `DEFAULT_CONFIG` n'est appliqué qu'à la
 * CRÉATION de la configuration (`config-api/store.ts`), jamais fusionné dans une configuration
 * déjà enregistrée. Tout site installé avant l'arrivée de ces libellés n'a donc pas la clé
 * `ui.displayLabels` — et c'est ce tableau, et non la console, qui décide de ce qui s'affiche.
 *
 * Un repli monolingue en français annulait donc la fonctionnalité là où elle sert le plus :
 * un écran réglé en arabe seul continuait d'afficher « Ticket » et « Aucun ticket en service ».
 */
const LIBELLES_DEFAUT = {
  waiting: { ar: 'في الانتظار', fr: 'en attente', en: 'waiting' },
  ticketsHeader: { ar: 'التذاكر', fr: 'Ticket', en: 'Ticket' },
  noTicket: {
    ar: 'لا توجد تذكرة قيد الخدمة حاليا',
    fr: 'Aucun ticket en service actuellement',
    en: 'No ticket currently being served',
  },
};

/**
 * Libellé d'écran dans une langue : la valeur de la console si elle existe, sinon le repli
 * DE CETTE LANGUE.
 */
export function libelleEcran(config, cle, lang = 'fr') {
  const k = String(lang).slice(0, 2).toLowerCase();
  const defaut = LIBELLES_DEFAUT[cle]?.[k] || LIBELLES_DEFAUT[cle]?.fr || '';
  return libelle(config?.ui?.displayLabels?.[cle], k, defaut);
}

/**
 * Langue du CODE DE TICKET — décision propre, distincte de l'ordre des noms de service.
 *
 * Les deux réglages ont été confondus dans une première version : le code suivait la première
 * langue affichée. Un exploitant qui plaçait l'anglais en tête pour la mise en page voyait donc
 * ses tickets basculer en anglais sans l'avoir demandé — et rien, dans l'écran de réglage, ne
 * laissait deviner le lien. Ce sont deux besoins différents : l'ordre des noms règle une mise
 * en page, le code de ticket règle ce que le client doit RECONNAÎTRE sur le papier, à l'écran
 * et à l'oreille.
 *
 * `auto` (défaut) conserve l'ancien comportement — la première langue affichée — pour que les
 * installations existantes ne changent pas d'aspect toutes seules.
 */
export function langueCodeTicket(config, langueAffichee = 'fr') {
  const choisi = config?.display?.ticketCodeLang;
  return ['ar', 'fr', 'en'].includes(choisi) ? choisi : langueAffichee;
}
